import React from "react";
import _ from "lodash";
import { format, formatCurrency } from "lib/globalize-util";
import PaymentGroupModel from "models/payments/payment-group";
import TransactionModel from "models/payments/transaction";
import OrderModel from "models/order";
import OrdersIcon from "components/icons/orders";
import { theme } from "styles/theme";
import { merge, $ } from "glamor";
import { DATATEST } from "constants/integration";
import AmpersandModel from "ampersand-model";
import CreditCardIcon from "material-ui/svg-icons/action/credit-card";

const muiStyles = {
    orderIcon: {
        marginRight: "10px",
    },
};

const styles = merge(
    $(" .paymentSummary", {
        backgroundColor: "#F5F5F5",
        margin: "16px 0",
        padding: "10px",
    }),
    $(" .paymentSummary > div", {
        fontWeight: "bold",
        textTransform: "uppercase",
        lineHeight: "22px",
        display: "flex",
    }),
    $(" dl", {
        display: "flex",
        padding: "4px 0",
    }),
    $(" dl dt", {
        flexGrow: "1",
    }),
    $(" .amountPaid", {
        fontWeight: "bold",
        borderBottom: "1px solid #585858",
        color: "#585858",
    }),
    $(" .divider", {
        fontWeight: "normal",
        borderBottom: "1px solid #585858",
    }),
    $(" .amountOutStanding", {
        marginBottom: "10px",
    }),
    $(" .headlineContainer", {
        margin: "16px 0px",
    }),
    $(" .headline", {
        color: "#585858",
        margin: "0px 0px 16px 0",
    }),
    $(" .bold", {
        fontWeight: "bold",
    }),
    $(" .supplemental", {
        margin: "6px 0px",
    })
);

const getMatchingPaymentGroup = (
    transaction: typeof PaymentGroupModel,
    paymentGroups: typeof PaymentGroupModel
): typeof PaymentGroupModel => {
    const matchingGroup = paymentGroups.find((paymentGroup) => {
        if (!paymentGroup) {
            return false;
        }
        return paymentGroup.transactions?.find((trans) => trans._id === transaction._id);
    });

    return matchingGroup || {};
};

const validatePaymentGroup = ({
    running_subtotal,
    all_shipping_fees,
    balance_remaining,
}: typeof PaymentGroupModel): boolean => {
    return _.isNumber(running_subtotal) && _.isNumber(all_shipping_fees) && _.isNumber(balance_remaining);
};

const getMatchingOrder = (paymentGroup: typeof PaymentGroupModel, orders: typeof OrderModel): typeof OrderModel => {
    return orders.find((order) => {
        if (!order) {
            return false;
        }
        const orderGroupId = order.order_group_id || order.__order_group_id;
        return orderGroupId === paymentGroup.order_group_id;
    });
};

export interface PaymentSuccessContentProps {
    transactions: typeof TransactionModel;
    paymentGroups: typeof PaymentGroupModel;
    orders: typeof OrderModel;
    selections?: any[];
    isMobile?: boolean;
}

export const PaymentSuccessContent = ({
    transactions,
    paymentGroups,
    orders,
    selections = [],
    isMobile = false,
}: PaymentSuccessContentProps) => {
    if (isMobile) {
        const skippingAll = (selections || []).every(({ selection }) => selection === "skip");

        if (skippingAll) {
            return (
                <div>
                    <div className="headlineContainer">
                        <p className="headline">{format("Order(s) updated. No payment was applied.")}</p>
                    </div>
                </div>
            );
        }
    }

    if (!isMobile && (!transactions.length || !paymentGroups.length || !orders.length)) {
        return null;
    }

    const txProcessedOrderNumbers = [];

    const transactionSummaries = transactions.map((transaction, index) => {
        if (!transaction?.type) {
            return null;
        }
        if (transaction.type === "save") {
            return null;
        }
        const paymentGroup = getMatchingPaymentGroup(transaction, paymentGroups);
        let paymentGroupModel;
        paymentGroup.orders = [];
        const isValidPaymentGroup = validatePaymentGroup(paymentGroup);
        if (paymentGroup instanceof AmpersandModel || isValidPaymentGroup) {
            paymentGroupModel = paymentGroup;
        } else {
            paymentGroupModel = new PaymentGroupModel(paymentGroup);
        }
        const order = getMatchingOrder(paymentGroup, orders);
        paymentGroupModel.set("workingOrder", order);
        txProcessedOrderNumbers.push(order.order_number);

        let prevPaid = paymentGroupModel.balance_committed_to_pay;

        if (transaction.type === "payment" || transaction.type === "capture") {
            prevPaid = prevPaid - transaction.amount;
        }

        const paidToday = transaction.amount && (transaction.type === "payment" || transaction.type === "capture");

        return (
            <li key={index} className="paymentSummary">
                <div>
                    <OrdersIcon color={theme.palette.darkGrey} style={muiStyles.orderIcon} />
                    {format("Summary for Order #|orderNumber|", { orderNumber: order.order_number })}
                </div>
                <ul>
                    <li>
                        <dl className="cf">
                            <dt>{format("Order Sub Total")}</dt>
                            <dd>{formatCurrency(paymentGroupModel.running_subtotal, order.currency_code)}</dd>
                        </dl>
                    </li>
                    <li>
                        <dl className={transaction.type === "authorize" ? "cf divider" : "cf"}>
                            <dt>{format("Total Shipping Amount")}</dt>
                            <dd>{formatCurrency(paymentGroupModel.all_shipping_fees, order.currency_code)}</dd>
                        </dl>
                    </li>
                    {prevPaid > 0 && (
                        <li>
                            <dl className="cf">
                                <dt>{format("Amount Prev Paid")}</dt>
                                <dd>{formatCurrency(prevPaid.toFixed(2) * -1, order.currency_code)}</dd>
                            </dl>
                        </li>
                    )}
                    {paidToday && (
                        <li>
                            <dl className="cf amountPaid">
                                <dt>{format("Amount Paid Today")}</dt>
                                <dd>{formatCurrency(transaction.amount.toFixed(2) * -1, order.currency_code)}</dd>
                            </dl>
                        </li>
                    )}
                    <li>
                        <dl className="cf amountOutStanding">
                            <dt>{format("Amount Outstanding")}</dt>
                            <dd>{formatCurrency(paymentGroupModel.balance_remaining, order.currency_code)}</dd>
                        </dl>
                    </li>
                </ul>
                <dl className="cf">
                    <dt>{format("Payment Method")}</dt>
                    <dd>{`${
                        transaction.payment_method === "cc"
                            ? transaction.card_type?.toUpperCase()
                            : transaction.bank_name?.toUpperCase()
                    } **${transaction.last_four}`}</dd>
                </dl>
            </li>
        );
    });

    let mobileViewCardsSaved = [];
    if (isMobile) {
        mobileViewCardsSaved = selections
            .map(({ selection }, index) => {
                if (selection === "save") {
                    const orderNumber = (orders.models || orders)[index].order_number;
                    return (
                        <li className="cardSaved icon icon-checkmark" key={index}>
                            {format("Card saved for order |orderNumber|", { orderNumber })}
                        </li>
                    );
                } else {
                    return null;
                }
            })
            .filter((el) => !!el);
    }

    return (
        <div {...styles}>
            {txProcessedOrderNumbers.length > 0 && (
                <div className="headlineContainer" data-testId={DATATEST.ORDERS.PAYMENT_PROCESSED_FOR_ORDERS}>
                    <p className="headline">
                        {format("Payment was successfully processed for order(s): |orderNumbers|", {
                            orderNumbers: txProcessedOrderNumbers.join(", "),
                        })}
                    </p>
                    <p className="supplemental">{format("You will shortly receive an order confirmation email.")}</p>
                </div>
            )}
            {mobileViewCardsSaved.length > 0 && (
                <div className="paymentSummary">
                    <div>
                        <CreditCardIcon color={theme.palette.darkGrey} style={muiStyles.orderIcon} />
                        {format("Cards Saved:")}
                    </div>
                    <ul className="mobileCardsSaved">{mobileViewCardsSaved}</ul>
                </div>
            )}
            <ul>{transactionSummaries}</ul>
        </div>
    );
};
